import React, { useRef, useEffect, useState } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Tooltip } from "../../../ui/tooltip";
import { Eye, Flag, Plus, Wrench } from "lucide-react";
import { validateNode, operatorNeedsValue } from "../utils/validation";
import { renderContentWithPlaceholders } from "../utils/utils";
import { CONVERSATION_TREE_TOOLTIPS } from "../utils/tooltips";

const CustomNode: React.FC<NodeProps> = ({ id, data }) => {
  const {
    isPlaceholder = false,
    hasChildren = false,
    entityType,
    isDestinationNode = false,
    isDescendantOfDestination = false,
    placeholders = {},
    globalRegistry = {},
    decisionVariablesRegistry = {},
    flowValidationResult = null,
  } = data;

  // After rendering, we need to update the node dimensions
  const nodeRef = useRef<HTMLDivElement>(null);
  
  // Hover state for + icons
  const [showPlusIcons, setShowPlusIcons] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Update node dimensions after rendering
    if (nodeRef.current) {
      const height = nodeRef.current.offsetHeight;
      if (typeof data.onNodeResize === "function") {
        data.onNodeResize(id, height);
      }
    }
    // Force re-render of plus icon positions when content changes
  }, [id, data, data.content, data.decisionData, data.toolCalls, entityType]);
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Handle mouse enter - start hover delay
  const handleMouseEnter = () => {
    // Clear any existing timeouts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPlusIcons(true);
    }); // 1 second delay
  };
  
  // Validate the node
  const validation = validateNode(data, placeholders, globalRegistry, decisionVariablesRegistry);
  
  // Check for duplicate identifier warning
  const hasDuplicateIdentifier = data.duplicateIdentifierInfo && data.duplicateIdentifierInfo.nodeIds.length > 1;
  
  // Check if there are flow-level validation issues (like missing identifier for decision child)
  const hasFlowValidationIssues = flowValidationResult && !flowValidationResult.validation.isValid;
  
  // Get CSS classes for node styling based on state and type
  const getNodeClasses = () => {
    const bgClass = "bg-card";
    let borderClass = "border-border";
    let borderWidth = "border";
    
    // Handle placeholder nodes first
    if (isPlaceholder) {
      borderClass = "border-blue-500";
      borderWidth = "border-2 border-dashed";
    }
    // Handle validation states (highest priority)
    else if (!validation.isValid || hasDuplicateIdentifier || hasFlowValidationIssues) {
      borderClass = "border-destructive";
      borderWidth = "border-2";
    }
    // Handle destination nodes
    else if (isDestinationNode) {
      borderClass = "border-orange-500";
      borderWidth = "border-2";
    }
    // Handle potential start nodes
    else if (data.potentialStart && entityType === "BOT") {
      borderClass = "border-sky-500";
      borderWidth = "border";
    }
    // Handle descendants of destination
    else if (isDescendantOfDestination) {
      borderClass = "border-yellow-500";
      borderWidth = "border";
    }
    // Handle different entity types
    else if (entityType === "DECISION") {
      borderClass = "border-purple-600";
      borderWidth = "border";
    }
    else if (entityType === "BOT") {
      borderClass = "border-blue-500";
      borderWidth = "border";
    }
    else if (entityType === "USER") {
      borderClass = "border-green-500";
      borderWidth = "border";
    }
    
    return `${bgClass} ${borderClass} ${borderWidth}`;
  };
  
  // Calculate exact Handle positioning for perfect edge alignment
  const getHandlePosition = () => {
    const padding = 2;
    const hasThickBorder = !validation.isValid || hasDuplicateIdentifier || hasFlowValidationIssues || isDestinationNode || isPlaceholder;
    const borderWidth = hasThickBorder ? 2 : 1; // border-2 = 2px, border = 1px
    
    let edgeOffset = padding + borderWidth; // Position at outer edge of border
    
    // For thick borders, add a small adjustment so the handle sits just outside the border
    if (!validation.isValid || hasFlowValidationIssues) {
      edgeOffset += 15;
    }
    return edgeOffset;
  };

  // Calculate consistent plus icon positioning for all node types and states
  const getPlusIconPosition = (position: "bottom" | "right") => {
    const VISUAL_GAP = 24; // Clear space between node border and button edge
    const BUTTON_RADIUS = 12; // Half of button size (24px / 2)
    
    // Check if this node has thick borders (error states)
    const hasThickBorder = !validation.isValid || hasDuplicateIdentifier || hasFlowValidationIssues || isDestinationNode || isPlaceholder;
    const borderAdjustment = hasThickBorder ? 2 : 0; // Extra spacing for thick borders
    
    const baseSpacing = VISUAL_GAP + BUTTON_RADIUS + borderAdjustment;
    
    if (position === "right") {
      if (hasThickBorder) {
        return baseSpacing + 12;
      }
      return baseSpacing + 16;
    }
    
    if (position === "bottom") {
      if (hasThickBorder) {
        return baseSpacing + 16;
      }
      return baseSpacing + 16; // Increased margin for normal nodes
    }
    
    return baseSpacing;
  };
  
  // Create node content wrapped in a tooltip if there are validation issues
  const nodeContent = (
    <>
      <Handle 
        type="target" 
        position={Position.Top} 
        id="target" 
        style={{ top: `-${getHandlePosition()}px` }}
      />
      
      {/* Node controls - only show preview button for non-placeholder nodes */}
      {!isPlaceholder && (
        <div className="absolute top-1.5 right-1.5 flex gap-2 z-10">
          {/* Flag icon for potential start nodes */}
          {data.potentialStart && entityType === "BOT" && (
            <Tooltip content={CONVERSATION_TREE_TOOLTIPS.POTENTIAL_START_FLAG} side="left" immediate={true}>
              <div>
                <Flag className="text-maitai-green w-3.5 h-3.5 p-0.5" />
              </div>
            </Tooltip>
          )}
          <Button 
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              data.onPreviewConversation();
            }}
          >
            <Eye className="w-3 h-3 text-muted-foreground hover:text-foreground transition-colors" />
          </Button>
        </div>
      )}
      
              {/* Header with entity type only */}
      <div className="mb-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm font-gc-med">
            {entityType}
          </Badge>
        </div>
      </div>
      
      {/* Content area */}
      <div className="border-t border-border/10 pt-2 break-words">
        {!isPlaceholder ? (
          entityType === "DECISION" ? (
            // Special rendering for decision nodes
            <div className="text-xs">
              {Array.isArray(data.decisionData?.conditions) && data.decisionData.conditions.length > 0 ? (
                <>
                  <div className="mb-1.5 p-1.5 bg-muted rounded-sm text-xs text-foreground break-words">
                    <strong>Conditions:</strong>
                    <ul className="pl-4 m-0">
                      {data.decisionData.conditions.map((cond: any, idx: number) => {
                        let operatorLabel = cond.operator;
                        // Make operators more readable
                        if (cond.operator === "is_true") operatorLabel = "is true";
                        else if (cond.operator === "is_false") operatorLabel = "is false";
                        else if (cond.operator === "is_null") operatorLabel = "is null";
                        else if (cond.operator === "is_not_null") operatorLabel = "is not null";
                        else if (cond.operator === "not_contains") operatorLabel = "does not contain";
                        else if (cond.operator === "starts_with") operatorLabel = "starts with";
                        else if (cond.operator === "ends_with") operatorLabel = "ends with";
                        else if (cond.operator === "equals") operatorLabel = "equals";
                        else if (cond.operator === "not_equals") operatorLabel = "does not equal";
                        else if (cond.operator === "greater_than") operatorLabel = "greater than";
                        else if (cond.operator === "less_than") operatorLabel = "less than";
                        else if (cond.operator === "greater_than_or_equal") operatorLabel = "greater than or equal";
                        else if (cond.operator === "less_than_or_equal") operatorLabel = "less than or equal";
                        return (
                          <li key={cond.id || idx} className="mb-0.5 list-none">
                            {idx > 0 && (
                              <span className="text-maitai-green font-semibold mr-1">{cond.joiner?.toUpperCase() || "AND"}</span>
                            )}
                            <code className="break-words">{cond.variable || <span className="text-muted-foreground">[variable]</span>}</code> {operatorLabel}
                            {operatorNeedsValue(cond.operator) && (
                              <> <code className="break-words">{cond.value || <span className="text-muted-foreground">[value]</span>}</code></>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </>
              ) : data.decisionData?.condition?.variable ? (
                <>
                  <div className="mb-1.5 p-1.5 bg-muted rounded-sm text-xs text-foreground break-words">
                    <strong>Condition:</strong> If <code className="break-words">{data.decisionData.condition.variable}</code> {data.decisionData.condition.operator}
                    {data.decisionData.condition.type === "value" && (
                      <> <code className="break-words">{data.decisionData.condition.value}</code></>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-muted-foreground italic opacity-60">
                  No condition configured
                </div>
              )}
            </div>
          ) : entityType === "BOT" && data.toolCalls && data.toolCalls.length > 0 ? (
            // Special rendering for BOT nodes with tool calls
            <div className="text-xs space-y-2 break-words">
              {data.toolCalls.map((toolCall: any, index: number) => (
                <div key={index} className="p-2 bg-muted/50 rounded-sm border border-border/30">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Wrench className="w-3 h-3 text-maitai-green" />
                    <span className="font-gc-med text-foreground">
                      {toolCall.name || "Unnamed Function"}
                    </span>
                  </div>
                  
                  {toolCall.parameters && toolCall.parameters.length > 0 && (
                    <div className="text-xs mt-1">
                      <div className="text-muted-foreground mb-1">Parameters:</div>
                      <div className="flex flex-wrap gap-1">
                        {toolCall.parameters.map((p: any, paramIndex: number) => (
                          <span key={paramIndex} className="text-xs bg-accent px-2 py-1 rounded font-gc-med break-all max-w-full">
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {toolCall.responseVariables && toolCall.responseVariables.length > 0 && (
                    <div className="text-xs mt-2">
                      <div className="text-muted-foreground mb-1">Response Variables:</div>
                      <div className="flex flex-wrap gap-1">
                        {toolCall.responseVariables.map((rv: any, rvIndex: number) => (
                          <span key={rvIndex} className="text-xs bg-accent px-2 py-1 rounded font-gc-med">
                            {rv.name} ({rv.values?.length || 0})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            renderContentWithPlaceholders(data.content, placeholders, globalRegistry, data.onEditPlaceholder)
          )
        ) : (
          <div className="text-foreground">{data.content}</div>
        )}
      </div>
      
      {/* Optional fields pills at bottom */}
      {!isPlaceholder && (
        <div className="flex flex-wrap gap-1.5 items-center mt-2 pt-2 border-t border-border/10">
          {/* Start node pill */}
          {data.potentialStart && entityType === "BOT" && (
            <Badge variant="outline" className="text-xs px-2 py-0.5 border-green-500/30 text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400">
              Marked as Start
            </Badge>
          )}
          
          {/* End node pill */}
          {data.isDestinationNode && (
            <Badge variant="outline" className="text-xs px-2 py-0.5 border-red-500/30 text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400">
              Marked as End
            </Badge>
          )}
          
          {/* Variables set pill */}
          {data.variableAssignments && data.variableAssignments.length > 0 && (
            <Badge variant="outline" className="text-xs px-2 py-0.5 border-blue-500/30 text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400">
              Variables Set ({data.variableAssignments.length})
            </Badge>
          )}
        </div>
      )}
      
      {/* Only show add button for non-placeholder, non-destination nodes */}
      {!isPlaceholder && !isDestinationNode && (() => {
        // For decision nodes, hide + button if both true and false paths are defined
        if (entityType === "DECISION") {
          const decisionData = data.decisionData;
          const hasBothPaths = decisionData?.truePath && decisionData?.falsePath;
          if (hasBothPaths) {
            return null; // Don't show + button for decision nodes with both paths
          }
        }
        
        return (
          <>
            {/* Show button below node if it has no children */}
            {!hasChildren && (
              <div 
                className={`absolute left-1/2 transform -translate-x-1/2 z-10 transition-all duration-300 ease-in-out ${
                  showPlusIcons ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
                }`}
                style={{ 
                  bottom: `-${getPlusIconPosition("bottom")}px`
                }}
                onMouseEnter={() => {
                  // Keep plus icons visible when hovering over them
                  if (hideTimeoutRef.current) {
                    clearTimeout(hideTimeoutRef.current);
                  }
                }}
                onMouseLeave={() => {
                  // Start hide timer when leaving plus button
                  hideTimeoutRef.current = setTimeout(() => {
                    setShowPlusIcons(false);
                  }, 2000);
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 w-6 p-0 rounded-full border-dashed bg-foreground text-background hover:bg-foreground/90 dark:bg-white dark:text-black dark:hover:bg-gray-100 border-foreground dark:border-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    // For decision nodes, always add a BOT node (since decisions route to responses)
                    // For other nodes, automatically determine the opposite entity type
                    const childEntityType = entityType === "DECISION" ? "BOT" : (entityType === "USER" ? "BOT" : "USER");
                    data.onAddNode(childEntityType);
                  }}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            )}
            
            {/* Show button to the right if node has children */}
            {hasChildren && (
              <div 
                className={`absolute top-1/2 transform -translate-y-1/2 z-10 transition-all duration-300 ease-in-out ${
                  showPlusIcons ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
                }`}
                style={{ 
                  right: `-${getPlusIconPosition("right")}px`
                }}
                onMouseEnter={() => {
                  // Keep plus icons visible when hovering over them
                  if (hideTimeoutRef.current) {
                    clearTimeout(hideTimeoutRef.current);
                  }
                }}
                onMouseLeave={() => {
                  // Start hide timer when leaving plus button
                  hideTimeoutRef.current = setTimeout(() => {
                    setShowPlusIcons(false);
                  }, 1000);
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 w-6 p-0 rounded-full border-dashed bg-foreground text-background hover:bg-foreground/90 dark:bg-white dark:text-black dark:hover:bg-gray-100 border-foreground dark:border-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    // For decision nodes, always add a BOT node (since decisions route to responses)
                    // For other nodes, automatically determine the opposite entity type
                    const childEntityType = entityType === "DECISION" ? "BOT" : (entityType === "USER" ? "BOT" : "USER");
                    data.onAddNode(childEntityType);
                  }}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            )}
          </>
        );
      })()}
      
      {/* Show handles for non-destination nodes */}
      {!isDestinationNode && (
        <>
          {entityType === "DECISION" ? (
            // Decision nodes get two bottom handles: True and False both centered
            <>
              <Handle 
                type="source" 
                position={Position.Bottom} 
                id="true" 
                style={{ bottom: `-${getHandlePosition()}px` }}
              />
              <Handle 
                type="source" 
                position={Position.Bottom} 
                id="false" 
                style={{ bottom: `-${getHandlePosition()}px` }}
              />
            </>
          ) : (
            // Regular nodes get bottom handle
            <Handle 
              type="source" 
              position={Position.Bottom} 
              id="source" 
              style={{ bottom: `-${getHandlePosition()}px` }}
            />
          )}
        </>
      )}
    </>
  );
  
  // Render simple placeholder differently
  if (isPlaceholder) {
    return (
      <div
        ref={nodeRef}
        className="group cursor-pointer transition-colors p-6 rounded-lg bg-card border border-border relative w-[250px]"
        onClick={data.onPlaceholderClick}
      >
        <Handle 
          type="target" 
          position={Position.Top} 
          id="target" 
          style={{ top: `-${getHandlePosition()}px` }}
        />
        <div className="text-center">
          <p className="text-base font-gc-reg text-foreground group-hover:text-maitai-green transition-colors">
            {data.content}
          </p>
        </div>
        <Handle 
          type="source" 
          position={Position.Bottom} 
          id="source" 
          style={{ bottom: `-${getHandlePosition()}px` }}
        />
      </div>
    );
  }

  // Prevent opening the modal when the node is being dragged
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef<boolean>(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pointerDownRef.current) return;
    const dx = Math.abs(e.clientX - pointerDownRef.current.x);
    const dy = Math.abs(e.clientY - pointerDownRef.current.y);
    if (dx > 4 || dy > 4) {
      isDraggingRef.current = true;
    }
  };

  const handleMouseUp = () => {
    // Only reset flags; actual open happens on onClick when not dragging
    pointerDownRef.current = null;
    // Don't immediately reset dragging here to allow onClick to read the value
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent opening if a drag was detected
    if (isDraggingRef.current) {
      // reset and ignore
      isDraggingRef.current = false;
      return;
    }
    if (typeof data.onEditNode === "function") {
      data.onEditNode();
      e.stopPropagation();
    }
  };

  const handleMouseLeave = () => {
    // Reset tracking if pointer leaves the node
    pointerDownRef.current = null;
    isDraggingRef.current = false;
    
    // Clear hover timeout if still waiting to show
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Hide plus icons after 2 seconds to allow clicking
    if (showPlusIcons) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowPlusIcons(false);
      }, 2000); // 2 second persist delay
    }
  };

  return (
    <div
      ref={nodeRef}
      className={`p-3.5 rounded-md w-[250px] cursor-pointer relative ${getNodeClasses()}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onMouseLeave={handleMouseLeave}
      onContextMenu={data.onContextMenu}
      onMouseEnter={handleMouseEnter}
    >
      {(!validation.isValid || hasDuplicateIdentifier || hasFlowValidationIssues) ? (
        <Tooltip
          content={(() => {
            const allIssues: string[] = [];
            if (hasDuplicateIdentifier) {
              allIssues.push(`Duplicate identifier "${data.identifier}" is used by ${data.duplicateIdentifierInfo.nodeIds.length} nodes`);
            }
            if (!validation.isValid && validation.issues.length > 0) {
              allIssues.push(...validation.issues);
            }
            if (hasFlowValidationIssues && flowValidationResult?.validation?.issues) {
              allIssues.push(...flowValidationResult.validation.issues);
            }
            return allIssues.join("\n");
          })()}
          side="left"
          delayLong={true}
        >
          {nodeContent}
        </Tooltip>
      ) : (
        nodeContent
      )}
    </div>
  );
};

export default CustomNode; 